import React, { MutableRefObject, ReactNode, useEffect, useState } from 'react';
import SimpleBarCore from 'simplebar-core';
import SimpleBar from 'simplebar-react';
import styled from 'styled-components';
import { Flexer } from '../app/dashboard/elements/LayoutElements';
import { remInPx } from './Utils';

export interface ScrollerValues {
    clientWidth: number;
    clientHeight: number;
    scrollTop: number;
    scrollHeight: number;
    top: number;
}

export const ScrollerValues = {
    for(elmt: HTMLElement): ScrollerValues {
        return {
            clientWidth: elmt.clientWidth,
            clientHeight: elmt.clientHeight,
            scrollTop: elmt.scrollTop,
            scrollHeight: elmt.scrollHeight,
            top: elmt.scrollTop / (elmt.scrollHeight - elmt.clientHeight),
        };
    }
};

export type ScrollerInteral = SimpleBarCore;

export type FlexScrollerProps = {
    internalRef?: MutableRefObject<ScrollerInteral>,
    autoHide?: boolean,
    shadow?: boolean,
    maxHeight?: string | number,
    children: ReactNode,
    onScroll?: (values: ScrollerValues) => void,
};

const Shadow = styled.div<{ visible: boolean }>`
    position: absolute;
    bottom: 0rem;
    left: 0;
    right: 0;
    height: 5rem;
    pointer-events: none;
    opacity: ${p => p.visible ? 1 : 0};
    transition: opacity 0.2s linear;
    overflow: hidden;

    &:after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 0rem;
        pointer-events: none;
        box-shadow: 0 0 .5rem .125rem rgba(0, 0, 0, 0.05);
    }
`;

const ShadowMount = React.memo(({ core }: { core: SimpleBarCore }) => {
    const scrollElmt = core?.getScrollElement();
    const [showShadow, setShowShadow] = useState(false);
    useEffect(() => {
        if (!scrollElmt) return;
        const fn = () => {
            const hasScrollArea = scrollElmt.scrollHeight > scrollElmt.clientHeight;
            const isNearBottom = scrollElmt.scrollHeight - (scrollElmt.scrollTop + scrollElmt.clientHeight) < 10;
            setShowShadow(hasScrollArea && !isNearBottom);
        };
        fn(); // Call initially
        scrollElmt.addEventListener('scroll', fn);
        scrollElmt.addEventListener('scrollend', fn);
        return () => {
            scrollElmt.removeEventListener('scroll', fn);
            scrollElmt.removeEventListener('scrollend', fn);
        };
    }, [scrollElmt]);

    return <Shadow visible={showShadow} />;
});

export const FlexScroller = ({ internalRef, autoHide, shadow, maxHeight, children, onScroll }: FlexScrollerProps) => {
    onScroll = onScroll ?? (() => { });
    const [core, setCore] = useState<SimpleBarCore>();

    useEffect(() => {
        if (!core) return;
        if (internalRef) internalRef.current = core;
        const sh = (e) => onScroll(ScrollerValues.for(e.target as HTMLElement));
        core.getScrollElement().addEventListener('scroll', sh);
        return () => core.getScrollElement().removeEventListener('scroll', sh);
    }, [core]);

    const scrollerStyle = {
        height: '100%',
        maxHeight: !!maxHeight ? remInPx(maxHeight) : 'auto'
    };

    return <Flexer className="blockWheel" relative grow>
        <Flexer absolute inset="0">
            <SimpleBar ref={setCore} autoHide={autoHide ?? true} style={scrollerStyle}>
                {children}
            </SimpleBar>
            {shadow && <ShadowMount core={core} />}
        </Flexer>
    </Flexer>;
};

export type FixedScrollerProps = FlexScrollerProps & { height?: string | number };

export const FixedScroller = ({ internalRef, autoHide, height, maxHeight, children, onScroll }: FixedScrollerProps) => {
    onScroll = onScroll ?? (() => { });
    const [core, setCore] = useState<SimpleBarCore>();

    useEffect(() => {
        if (!core) return;
        if (internalRef) internalRef.current = core;
        const sh = (e) => onScroll(ScrollerValues.for(e.target as HTMLElement));
        core.getScrollElement().addEventListener('scroll', sh);
        return () => core.getScrollElement().removeEventListener('scroll', sh);
    }, [core]);

    const scrollerStyle = {
        width: '100%',
        height: '100%',
        maxHeight: !!maxHeight ? remInPx(maxHeight) : 'auto'
    };

    return <Flexer className="blockWheel" height={height} maxHeight={maxHeight}>
        <SimpleBar ref={setCore} autoHide={autoHide ?? true} style={scrollerStyle}>
            {children}
        </SimpleBar>
    </Flexer>;
};
